import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/esm/Add';
import Delete from '@mui/icons-material/esm/Delete';
import Edit from '@mui/icons-material/esm/Edit';
import PlaylistAddCheck from '@mui/icons-material/esm/PlaylistAddCheck';
import Refresh from '@mui/icons-material/esm/Refresh';
import { DataTable, Column } from '../../components/common';
import { PageHeader } from '../../components/layout';
import { RootState } from '../../store';
import qcChecklistService, { QcChecklistItem, QcTemplate, QcTemplatePayload } from '../../services/api/qcChecklistService';

const taskTypeOptions = ['formwork', 'reinforcement', 'concrete_pour', 'finishing', 'masonry', 'mep', 'handover'];

const emptyTemplate = (projectId: string | null): QcTemplatePayload => ({
  projectId: projectId ?? null,
  name: '',
  description: '',
  appliesToTaskType: null,
  items: [{ text: '', critical: false, requiresEvidence: false }],
});

const labelize = (value: string) => value.replace(/_/g, ' ');

export default function QcTemplatesPage() {
  const { activeProjectId, projects } = useSelector((state: RootState) => state.projects);
  const [templates, setTemplates] = useState<QcTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<QcTemplate | null>(null);
  const [form, setForm] = useState<QcTemplatePayload>(emptyTemplate(null));

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setError(null);
      const rows = await qcChecklistService.listTemplates(activeProjectId ? { projectId: activeProjectId } : {});
      setTemplates(rows);
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load QC templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProjectId]);

  const allTaskTypes = useMemo(() => Array.from(new Set([
    ...taskTypeOptions,
    ...templates.map((template) => template.appliesToTaskType).filter(Boolean) as string[],
  ])), [templates]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyTemplate(activeProjectId));
    setDialogOpen(true);
  };

  const openEdit = (template: QcTemplate) => {
    setEditing(template);
    setForm({
      projectId: template.projectId,
      name: template.name,
      description: template.description ?? '',
      appliesToTaskType: template.appliesToTaskType,
      items: template.items.length > 0 ? template.items : [{ text: '', critical: false, requiresEvidence: false }],
    });
    setDialogOpen(true);
  };

  const saveTemplate = async () => {
    const payload = {
      ...form,
      projectId: form.projectId || null,
      description: form.description || null,
      appliesToTaskType: form.appliesToTaskType || null,
      items: form.items.map((item) => ({
        text: item.text.trim(),
        critical: Boolean(item.critical),
        requiresEvidence: Boolean(item.requiresEvidence),
      })),
    };

    try {
      if (editing) {
        await qcChecklistService.updateTemplate(editing.id, payload);
      } else {
        await qcChecklistService.createTemplate(payload);
      }
      setDialogOpen(false);
      await loadTemplates();
    } catch (err: any) {
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to save QC template');
    }
  };

  const updateItem = (index: number, patch: Partial<QcChecklistItem>) => {
    setForm((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item),
    }));
  };

  const removeItem = (index: number) => {
    setForm((current) => ({
      ...current,
      items: current.items.filter((_item, itemIndex) => itemIndex !== index),
    }));
  };

  const columns: Column<QcTemplate>[] = [
    { id: 'name', label: 'Template', minWidth: 220 },
    {
      id: 'projectId',
      label: 'Scope',
      minWidth: 130,
      format: (value: string | null) => value ? projects.find((project) => project.id === value)?.name ?? 'Project' : 'Org-wide',
    },
    {
      id: 'appliesToTaskType',
      label: 'Task Type',
      minWidth: 150,
      format: (value: string | null) => value ? labelize(value) : 'Any type',
    },
    {
      id: 'items',
      label: 'Items',
      minWidth: 90,
      align: 'center',
      format: (value: QcChecklistItem[]) => value.length,
    },
    { id: 'version', label: 'Version', minWidth: 90, align: 'center' },
    {
      id: 'isActive',
      label: 'Active',
      minWidth: 120,
      format: (value: boolean, row) => (
        <Switch size="small" checked={value} onChange={(event) => qcChecklistService.setTemplateActive(row.id, event.target.checked).then(loadTemplates)} />
      ),
    },
    {
      id: 'actions',
      label: '',
      minWidth: 80,
      sortable: false,
      format: (_value, row) => (
        <Tooltip title="Edit template">
          <IconButton size="small" onClick={() => openEdit(row)}>
            <Edit fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="QC Templates"
        subtitle={`${templates.length} reusable checklists`}
        actions={
          <Stack direction="row" spacing={1}>
            <Tooltip title="Refresh">
              <IconButton onClick={loadTemplates}>
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
              New Template
            </Button>
          </Stack>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
        <DataTable columns={columns} data={templates} loading={loading} searchable pagination defaultRowsPerPage={10} />
      </Box>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Edit QC Template' : 'New QC Template'}</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: 2 }}>
          <TextField label="Name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
          <TextField label="Description" value={form.description ?? ''} onChange={(event) => setForm({ ...form, description: event.target.value })} multiline minRows={2} />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <TextField select label="Scope" value={form.projectId ?? ''} onChange={(event) => setForm({ ...form, projectId: event.target.value || null })}>
              <MenuItem value="">Org-wide</MenuItem>
              {projects.map((project) => <MenuItem key={project.id} value={project.id}>{project.name}</MenuItem>)}
            </TextField>
            <TextField select label="Task Type" value={form.appliesToTaskType ?? ''} onChange={(event) => setForm({ ...form, appliesToTaskType: event.target.value || null })}>
              <MenuItem value="">Any type</MenuItem>
              {allTaskTypes.map((type) => <MenuItem key={type} value={type}>{labelize(type)}</MenuItem>)}
            </TextField>
          </Box>

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Items</Typography>
            <Button startIcon={<PlaylistAddCheck />} onClick={() => setForm({ ...form, items: [...form.items, { text: '', critical: false, requiresEvidence: false }] })}>
              Add Item
            </Button>
          </Stack>

          <Stack spacing={1.5}>
            {form.items.map((item, index) => (
              <Box key={index} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 130px 160px 44px' }, gap: 1, alignItems: 'center' }}>
                <TextField size="small" label={`Item ${index + 1}`} value={item.text} onChange={(event) => updateItem(index, { text: event.target.value })} />
                <FormControlLabel control={<Checkbox checked={item.critical} onChange={(event) => updateItem(index, { critical: event.target.checked })} />} label="Critical" />
                <FormControlLabel control={<Checkbox checked={item.requiresEvidence} onChange={(event) => updateItem(index, { requiresEvidence: event.target.checked })} />} label="Evidence" />
                <Tooltip title="Remove item">
                  <span>
                    <IconButton size="small" onClick={() => removeItem(index)} disabled={form.items.length === 1}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            ))}
          </Stack>

          {editing && (
            <Chip size="small" label={`Version ${editing.version}${form.items !== editing.items ? ' - item edits create next version' : ''}`} sx={{ justifySelf: 'start' }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveTemplate} disabled={!form.name.trim() || form.items.some((item) => !item.text.trim())}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
